import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-tight ' +
  'font-medium leading-none text-[12px] tracking-[0] ' +
  'transition-colors duration-instant ease-standard ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper dark:focus-visible:ring-offset-paper ' +
  'disabled:cursor-not-allowed disabled:opacity-40';

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-6 px-2.5',
  md: 'h-7 px-3',
  lg: 'h-8 px-3.5 text-[13px]',
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-ink text-paper hover:bg-ink-soft',
  accent: 'bg-accent text-accent-fg hover:bg-accent-sunk',
  secondary: 'border border-rule bg-paper text-ink hover:bg-paper-soft',
  ghost: 'text-ink-muted hover:bg-paper-soft hover:text-ink',
  danger: 'bg-danger text-paper hover:bg-danger-soft',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', iconLeft, iconRight, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      {...rest}
      className={cn(BASE, SIZE[size], VARIANT[variant], className)}
    >
      {iconLeft ? <span className="grid place-items-center [&>svg]:size-3.5">{iconLeft}</span> : null}
      {children}
      {iconRight ? <span className="grid place-items-center [&>svg]:size-3.5">{iconRight}</span> : null}
    </button>
  );
});
